import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import type { ICellRendererParams } from '@ag-grid-community/core';
import { Button, TextField, Paper } from '@material-ui/core';

import { toast } from 'react-toastify';
import { Overlay } from 'src/common-ui';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import ServiceContainer from 'src/ServiceContainer';

import { PlanMetadata } from 'src/state/scope/codecs/PlanMetadata';
import { clonePlans } from 'src/state/workingSets/workingSets.actions';
import { AppState, AppThunkDispatch } from 'src/store';
import {
  useHandleChangeStringVersion,
  usePublishVersionsToDropdowns,
  useSetSelectedStringVersion,
} from 'src/utils/Component/hooks/hooks';

import 'src/components/Mfp/InitializePlan/_InitializePlan.scss';
import './_ClonePlanRenderer.scss';
import { Autocomplete } from '@material-ui/lab';
import { KeyboardArrowDown } from '@material-ui/icons';
import TemplateModalWithButton from 'src/components/TemplateModalWithButton/TemplateModalWithButton';

const mapStateToProps = (state: AppState) => {
  const { scope } = state;
  return {
    scope,
    publishVersions: state.scopeManagement.publishVersions,
  };
};

const mapDispatchToProps = (dispatch: AppThunkDispatch) => {
  return {
    clonePlans: (planIds: number[], version: string) => {
      return dispatch(clonePlans({ planIds, version }));
    },
  };
};

interface ClonePlanRendererOwnProps extends ICellRendererParams {
  data: PlanMetadata;
  onClonePlan: () => void;
}

interface ClonePlanRendererProps
  extends ReturnType<typeof mapDispatchToProps>,
    ReturnType<typeof mapStateToProps>,
    ClonePlanRendererOwnProps {}

const ClonePlanRenderer = (props: ClonePlanRendererProps) => {
  const { onClonePlan, data, clonePlans, publishVersions } = props;
  const logger = ServiceContainer.loggingService;

  const loading = !publishVersions;
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined);
  const [clonePlanLoading, setClonePlanLoading] = useState<boolean>(false);

  // Material-UI data for the version dropdown
  const versionDropdowns = usePublishVersionsToDropdowns(publishVersions);
  useSetSelectedStringVersion(versionDropdowns, setSelectedVersion, selectedVersion);
  const handleChangeVersion = useHandleChangeStringVersion(setSelectedVersion);

  const versionLabelFormatter = (selected?: string) => {
    return versionDropdowns.find((version) => version.value === selected)?.text;
  };

  const handleSubmit = useCallback(async () => {
    if (selectedVersion && typeof selectedVersion === 'string') {
      setClonePlanLoading(true);
      const clonePlanResp = await clonePlans([data.id], selectedVersion);

      if (clonePlanResp.type === 'workingSets/clonePlan/rejected') {
        toast.error('An error occurred copying this plan', {
          position: toast.POSITION.TOP_LEFT,
        });
        logger.error(`An error occurred copying the plan id ${data.id} to ${selectedVersion}`);
        setClonePlanLoading(false);
      } else if (clonePlanResp.type === 'workingSets/clonePlan/fulfilled') {
        onClonePlan();
        setClonePlanLoading(false);
        // TemplateModalWithButton will close itself after onSubmit
      }
    }
  }, [selectedVersion, clonePlans, data.id, onClonePlan, logger]);

  const modal = (
    <TemplateModalWithButton
      title="Copy Plan"
      buttonComponent={
        <Button
          color="secondary"
          variant="contained"
          data-qa="clone-plan-modal"
          style={{ padding: '8px', minWidth: 'unset' }}
        >
          <FileCopyOutlinedIcon style={{ color: 'white' }} />
        </Button>
      }
      showSubmit
      onSubmit={handleSubmit}
      onCancel={() => {}}
      paperClassName="import-version-modal"
      customSubmitComponent={({ submit, cancel }) => (
        <>
          <SquareActionButton
            onClick={cancel}
            dataQa="close-modal"
            text="Cancel"
            type="secondary"
            className="button"
          />
          <SquareActionButton
            onClick={submit}
            className="button import-version-modal-button"
            dataQa="copy-plan-btn-copy"
            text="Submit"
          />
        </>
      )}
    >
      <div className="initialize-plan" data-qa="copy-plan-modal">
        {clonePlanLoading && <Overlay type="loading" visible fitParent />}
        <Paper
          className="clone-plan-segment"
          elevation={3}
          style={{ padding: '16px', marginBottom: '16px' }}
        >
          This action will copy the version "<strong>{versionLabelFormatter(data.version)}</strong>" to "
          <strong>{versionLabelFormatter(selectedVersion)}</strong>". Do you wish to proceed?
        </Paper>

        <div className="dropdown-group">
          <Autocomplete
            options={versionDropdowns}
            className={'autoComplete'}
            popupIcon={<KeyboardArrowDown className="teal-arrow" />}
            getOptionLabel={(option) => option.text as string}
            value={versionDropdowns.find((option) => option.value === selectedVersion) || undefined}
            onChange={(_, newValue) => {
              handleChangeVersion(_, newValue);
            }}
            disableClearable
            renderInput={(params) => <TextField {...params} variant="outlined" />}
          />
        </div>
      </div>
    </TemplateModalWithButton>
  );

  return <>{modal}</>;
};

export default connect(mapStateToProps, mapDispatchToProps)(ClonePlanRenderer);
